import { atom, type ReadableAtom, type WritableAtom } from 'nanostores';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import type { ChatHistoryItem } from '~/lib/persistence/chats';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import { getMemory } from '~/utils/memory';

export const enum SettingsPage {
  General = 'general',
  SubscriptionAndTokens = 'subscription-and-tokens',
  Backups = 'backups',
  Network = 'network',
  Applications = 'applications',
  Team = 'team',
  FeaturePreviews = 'feature-previews',
  KnowledgePersonal = 'knowledge-personal',
  PrivateRegistries = 'private-registries',
  PackagesKnowledge = 'packages-knowledge',
  KnowledgeTeam = 'knowledge-team',
  Project = 'project',
  Cloud = 'cloud',
  Domains = 'domains',
  KnowledgeProject = 'knowledge-project',
  DatabaseSection = 'database-section',
  Analytics = 'analytics',
  Authentication = 'authentication',
  UserManagement = 'user-management',
  FileStorage = 'file-storage',
  DatabaseGeneral = 'database-general',
  DatabaseStorage = 'database-storage',
  DatabaseAuth = 'database-auth',
  DatabaseUsers = 'database-users',
  DatabaseSecrets = 'database-secrets',
  DatabaseLogs = 'database-logs',
  DatabaseSuggestions = 'database-suggestions',
}

export type FatalErrorDialogVariant = 'out-of-memory' | 'webcontainer-startup-timeout' | 'webcontainer-startup-error';

export type DialogContent =
  | { type: 'delete'; item: ChatHistoryItem }
  | { type: 'settings'; initialPage?: SettingsPage; screen?: string }
  | { type: 'experimental-browser' }
  | { type: 'confirm-logout' }
  | { type: 'fatal-error'; error: Error; variant: FatalErrorDialogVariant }
  | { type: 'referrals' };

class MenuStore {
  #fatalErrorDialogShown = false;
  #dialogContent: WritableAtom<DialogContent | undefined> = atom();
  #dialogQueue: DialogContent[] = [];

  dialogContent: ReadableAtom<DialogContent | undefined> = this.#dialogContent;

  constructor() {
    this.#dialogContent.set(undefined);
    this.#dialogContent.listen((currentContent) => {
      if (!currentContent) {
        const nextDialog = this.#dialogQueue.shift();

        if (nextDialog) {
          this.#dialogContent.set(nextDialog);
        }
      }
    });
  }

  showFatalErrorDialog(variant: FatalErrorDialogVariant, error: Error) {
    if (this.#fatalErrorDialogShown) {
      return;
    }

    this.#fatalErrorDialogShown = true;

    this.openDialog({
      type: 'fatal-error',
      variant,
      error,
    });

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event:
          variant === 'out-of-memory'
            ? AnalyticsTrackEvent.OutOfMemory
            : variant === 'webcontainer-startup-timeout'
              ? AnalyticsTrackEvent.WebContainerStartupTimeout
              : AnalyticsTrackEvent.WebContainerStartupError,

        properties: {
          ...getMemory(),
          message: error.message,
          stack: error.stack,
        },
      },
    });
  }

  openDialog(dialogContent: DialogContent, immediate = false) {
    const currentContent = this.#dialogContent.get();

    if (!currentContent || immediate) {
      this.#dialogContent.set(dialogContent);
      return;
    }

    const duplicated = [currentContent, ...this.#dialogQueue].some(({ type }) => type === dialogContent.type);

    if (!duplicated) {
      this.#dialogQueue.push(dialogContent);
    }
  }

  closeDialog(cancelQueued = false) {
    if (cancelQueued) {
      this.#dialogQueue = [];
    }

    this.#dialogContent.set(undefined);
  }
}

export const menuStore = new MenuStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { menuStore });
}
